import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import deliverySlotApi from "../../services/deliverySlotApi";
import branchApi from "../../services/branchapi";
import "../../static/css/deliveryConfiguration.css";

const DeliverySlotForm = ({
  show,
  onHide,
  initialData,
  onSave,
  scopePrompt = false,
  targetBranchId = null,
  targetBranchName = "",
}) => {
  const [branches, setBranches] = useState([]);
  const [slots, setSlots] = useState([]);
  const [updateType, setUpdateType] = useState("SINGLE"); // SINGLE | ALL
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // only flag exact duplicates (same time AND same amount pair)
  const [duplicateIdx, setDuplicateIdx] = useState([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const result = await branchApi.getAllBranches({ status: 1 });
        setBranches(result.data.result || []);
      } catch (e) {
        setError("Failed to fetch branches.");
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    if (initialData) {
      setUpdateType("SINGLE");
      setSelectedBranchId(targetBranchId || initialData.branch_id || null);
      const withAmounts = (initialData.slots || []).map((s) => ({
        ...s,
        min_amount:
          s.min_amount != null ? Number(s.min_amount)
          : s.minAmount != null ? Number(s.minAmount)
          : 0,
        max_amount:
          s.max_amount != null ? Number(s.max_amount)
          : s.maxAmount != null ? Number(s.maxAmount)
          : 0,
      }));
      setSlots(withAmounts);
      checkDuplicates(withAmounts);
    } else {
      const seed = [{
        from_time: "",
        to_time: "",
        order_threshold: 30,
        status: 1,
        min_amount: 0,
        max_amount: 0, // 0 => ∞
      }];
      setSlots(seed);
      checkDuplicates(seed);
    }
  }, [initialData, targetBranchId]);

  const handleSlotChange = (index, key, value) => {
    const updated = [...slots];
    if (["order_threshold", "status", "min_amount", "max_amount"].includes(key)) {
      const num = value === "" ? "" : Number(value);
      updated[index][key] = num === "" ? "" : Number.isNaN(num) ? 0 : num;
    } else {
      updated[index][key] = value;
    }
    setSlots(updated);
    checkDuplicates(updated);
  };

  // ONLY mark as duplicate if time range AND amount pair are EXACTLY the same
  const checkDuplicates = (slotsToCheck) => {
    const dups = new Set();

    const rows = slotsToCheck.map((s, idx) => ({
      idx,
      id: s.id || null,
      from: s.from_time || "",
      to: s.to_time || "",
      min: Number(s.min_amount) || 0,
      max: Number(s.max_amount) || 0,
    }));

    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        const A = rows[i], B = rows[j];

        // same DB record during edit? skip
        if (A.id && B.id && A.id === B.id) continue;

        const sameTime = A.from === B.from && A.to === B.to;
        const sameAmount = A.min === B.min && A.max === B.max;

        if (sameTime && sameAmount) {
          dups.add(A.idx);
          dups.add(B.idx);
        }
      }
    }

    setDuplicateIdx([...dups]);
  };

  const addNewSlot = () => {
    const newSlots = [
      ...slots,
      {
        from_time: "",
        to_time: "",
        order_threshold: 30,
        status: 1,
        min_amount: 0,
        max_amount: 0,
      },
    ];
    setSlots(newSlots);
    checkDuplicates(newSlots);
  };

  const removeSlot = (index) => {
    const updated = [...slots];
    updated.splice(index, 1);
    setSlots(updated);
    checkDuplicates(updated);
  };

  const handleSubmit = async () => {
    setError(null);

    if (updateType === "SINGLE" && !selectedBranchId) {
      setError("Please select a branch.");
      return;
    }

    // Basic required & time ordering
    const invalidIndex = slots.findIndex(
      (slot) =>
        !slot.from_time ||
        !slot.to_time ||
        !slot.order_threshold ||
        slot.from_time >= slot.to_time
    );
    if (invalidIndex >= 0) {
      setError(`Slot at row ${invalidIndex + 1} has invalid time range or missing fields.`);
      return;
    }

    // Amount validation (>= 0; if max > 0 then max >= min; 0 means ∞ and allowed)
    const invalidAmtIdx = slots.findIndex((slot) => {
      const min = Number(slot.min_amount);
      const max = Number(slot.max_amount);
      if (!Number.isFinite(min) || min < 0) return true;
      if (!Number.isFinite(max) || max < 0) return true;
      if (max > 0 && max < min) return true;
      return false;
    });
    if (invalidAmtIdx >= 0) {
      setError(
        `Slot at row ${invalidAmtIdx + 1} has invalid amounts (must be ≥ 0; Max ≥ Min unless Max is 0 for ∞).`
      );
      return;
    }

    // Block exact duplicates
    if (duplicateIdx.length > 0) {
      setError("Duplicate slots found (same time & amount). Please change time or amount.");
      return;
    }

    const sourceBranchId = Number(initialData?.source_branch_id ?? initialData?.branch_id ?? selectedBranchId);
    const destinationBranchId = updateType === "GLOBAL" ? 0 : Number(selectedBranchId);
    const normalizedSlots = slots.map((slot) => ({
      ...(slot.id && sourceBranchId === destinationBranchId ? { id: slot.id } : {}),
      fromTime: slot.from_time,
      toTime: slot.to_time,
      orderThreshold: parseInt(slot.order_threshold),
      status: parseInt(slot.status),
      minAmount: Number(slot.min_amount) || 0,
      maxAmount: Number(slot.max_amount) || 0, // 0 => ∞
    }));

    const payload = updateType === "ALL"
      ? { update_type: "ALL", data: normalizedSlots }
      : updateType === "GLOBAL"
        ? { update_type: "GLOBAL", branch_id: 0, slots: normalizedSlots }
        : { update_type: "SINGLE", branch_id: selectedBranchId, slots: normalizedSlots };

    setSubmitting(true);
    try {
      await deliverySlotApi.addOrUpdateDeliverySlots(payload);
      if (onSave) onSave();
    } catch (e) {
      setError("Failed to save delivery slots.");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="delivery-slot-modal">
      <Modal.Header closeButton>
        <Modal.Title>Configure Delivery Slots</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="text-danger mb-2">{error}</div>}

        {scopePrompt && initialData?.is_inherited && (
          <div className="alert alert-info py-2">
            These slots are inherited from the All Branches default. Choose where the changes should be saved.
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>{scopePrompt ? "Save these slots for" : "Update Type"}</Form.Label>
          <Form.Select value={updateType} onChange={(e) => setUpdateType(e.target.value)}>
            <option value="SINGLE">Only {scopePrompt && targetBranchName ? targetBranchName : "Single Branch"}</option>
            {scopePrompt ? (
              <option value="GLOBAL">All Branches Default (branch_id = 0)</option>
            ) : (
              <option value="ALL">All Branches</option>
            )}
          </Form.Select>
          {scopePrompt && (
            <Form.Text className="text-muted">
              Branch-only creates an override. All Branches Default updates the fallback used by branches without their own slots.
            </Form.Text>
          )}
        </Form.Group>

        {updateType === "SINGLE" && !scopePrompt && (
          <Form.Group className="mb-3">
            <Form.Label>Select Branch</Form.Label>
            <Form.Select value={selectedBranchId || ""} onChange={(e) => setSelectedBranchId(e.target.value)}>
              <option value="">-- Select Branch --</option>
              {branches.map((branch) => (
                <option key={branch.branchId} value={branch.branchId}>
                  {branch.branchName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {(updateType === "SINGLE" || updateType === "ALL" || updateType === "GLOBAL") && (
          <>
            <h6 className="mb-3">Delivery Slots</h6>
            <div className="delivery-slot-grid delivery-slot-grid--header" aria-hidden="true">
              <span>Start Time</span>
              <span>End Time</span>
              <span>Order Threshold</span>
              <span>Min Amount</span>
              <span>Max Amount</span>
              <span>Status</span>
              <span>Action</span>
            </div>
            {slots.map((slot, index) => (
              <div key={index} className="delivery-slot-grid delivery-slot-grid--row">
                <Form.Group>
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={slot.from_time}
                    onChange={(e) => handleSlotChange(index, "from_time", e.target.value)}
                    className={duplicateIdx.includes(index) ? "is-invalid" : ""}
                    placeholder="From Time"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={slot.to_time}
                    onChange={(e) => handleSlotChange(index, "to_time", e.target.value)}
                    className={duplicateIdx.includes(index) ? "is-invalid" : ""}
                    placeholder="To Time"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Order Threshold</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={slot.order_threshold}
                    onChange={(e) => handleSlotChange(index, "order_threshold", e.target.value)}
                    placeholder="Order Threshold"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Min Amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={slot.min_amount ?? 0}
                    onChange={(e) => handleSlotChange(index, "min_amount", e.target.value)}
                    placeholder="Min Amount"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Max Amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={slot.max_amount ?? 0}
                    onChange={(e) => handleSlotChange(index, "max_amount", e.target.value)}
                    placeholder="Max Amount (0 = ∞)"
                  />
                  <Form.Text>0 = No limit</Form.Text>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={slot.status}
                    onChange={(e) => handleSlotChange(index, "status", e.target.value)}
                  >
                    <option value="1">Active</option>
                    <option value="2">Inactive</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="delivery-slot-action">
                  <Form.Label>Action</Form.Label>
                  <Button variant="outline-danger" onClick={() => removeSlot(index)} aria-label={`Remove slot ${index + 1}`}>
                    &times;
                  </Button>
                </Form.Group>

                {duplicateIdx.includes(index) && (
                  <div className="delivery-slot-error text-danger small">
                    Duplicate slot: same time range and same amount range already exists.
                  </div>
                )}
              </div>
            ))}
            <Button className="w-100 mt-2" variant="secondary" onClick={addNewSlot}>
              + Add Slot
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeliverySlotForm;
